import { useCallback, useMemo } from 'react';
import type { Preference } from 'sr-types/lib/search/v1/graphql';
import {
    parsePinnedSavedFiltersPreferenceValue,
    serializePinnedSavedFiltersPreferenceValue
} from '../../production/helpers/savedFilterPreferenceUtils';
import type { PreferenceInputs, PreferenceSuccessType } from '../auth/hooks/savePreferences';
import { SAVED_FILTERS_CONSTANTS, type PinnedSavedFiltersPreferenceValue } from '../types/savedFilters';

type UsePersonalPinnedTabsParams = {
    preferencesLoaded: boolean;
    preferences: Preference[];
    pinnedTabsPreferenceKey: string;
    accountId?: string;
    applicationName: string;
    savePreferences: (input: PreferenceInputs) => Promise<PreferenceSuccessType>;
};

type PersonalPinnedTabs = {
    preference: Preference;
    value: PinnedSavedFiltersPreferenceValue;
};

export function usePersonalPinnedTabs({
    preferencesLoaded,
    preferences,
    pinnedTabsPreferenceKey,
    accountId,
    applicationName,
    savePreferences
}: UsePersonalPinnedTabsParams) {
    const personalPinnedTabs = useMemo<PersonalPinnedTabs | null>(() => {
        if (!preferencesLoaded || !accountId) return null;
        const preference = preferences.find(
            (p) => p.key === pinnedTabsPreferenceKey && p.accountId === accountId && p.application === applicationName
        );
        if (!preference) return null;
        const value = parsePinnedSavedFiltersPreferenceValue(preference.value);
        return value ? { preference, value } : null;
    }, [preferencesLoaded, preferences, pinnedTabsPreferenceKey, accountId, applicationName]);

    const persistPersonalPinnedTabs = useCallback(
        async (nextPinnedValue: PinnedSavedFiltersPreferenceValue, currentPreferences: Preference[]) => {
            if (!accountId) return currentPreferences;

            const value = serializePinnedSavedFiltersPreferenceValue(nextPinnedValue);
            const result = await savePreferences({
                preferenceInput: {
                    ...(personalPinnedTabs?.preference.id && { identity: { id: personalPinnedTabs.preference.id } }),
                    accountId,
                    view: '',
                    key: pinnedTabsPreferenceKey,
                    value,
                    agent: '',
                    name: SAVED_FILTERS_CONSTANTS.PINNED_TABS_PREFERENCE_NAME
                },
                shareInput: null,
                showSuccessMessages: false
            });

            const existingPinnedPreference = personalPinnedTabs?.preference;
            const pinnedRow = existingPinnedPreference
                ? {
                      ...existingPinnedPreference,
                      id: result?.id || existingPinnedPreference.id,
                      value,
                      name: SAVED_FILTERS_CONSTANTS.PINNED_TABS_PREFERENCE_NAME
                  }
                : {
                      id: result?.id || '',
                      accountId,
                      application: applicationName,
                      key: pinnedTabsPreferenceKey,
                      value,
                      name: SAVED_FILTERS_CONSTANTS.PINNED_TABS_PREFERENCE_NAME,
                      shared: false,
                      view: '',
                      agent: '',
                      headline: '',
                      uri: ''
                  };

            return [
                ...currentPreferences.filter(
                    (p) =>
                        !(
                            p.key === pinnedTabsPreferenceKey &&
                            p.accountId === accountId &&
                            p.application === applicationName
                        )
                ),
                pinnedRow
            ];
        },
        [personalPinnedTabs?.preference, accountId, pinnedTabsPreferenceKey, savePreferences, applicationName]
    );

    return {
        personalPinnedTabs,
        persistPersonalPinnedTabs
    };
}
